import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { useLang } from '../../contexts/LangContext';
import { ImageUploadField } from './ImageUploadField';
import { getApiErrorMessage } from '../../utils/apiErrors';

interface LogoPathFieldProps {
  logoPath: string;
  onLogoPathChange: (logoPath: string) => void;
  label: string;
  pathHint: string;
  placeholder?: string;
  onUpload: (file: File) => Promise<void>;
  onRemove?: () => Promise<void>;
  uploadDisabled?: boolean;
  confirmRemove?: boolean;
}

export const LogoPathField: React.FC<LogoPathFieldProps> = ({
  logoPath,
  onLogoPathChange,
  label,
  pathHint,
  placeholder,
  onUpload,
  onRemove,
  uploadDisabled = false,
  confirmRemove = false,
}) => {
  const { t } = useLang();
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);

  const handleUpload = async (file: File) => {
    if (uploadDisabled) {
      toast.error(t('buildingLogoUploadSelectOperator'));
      return;
    }

    setUploading(true);
    try {
      await onUpload(file);
      toast.success(t('buildingLogoUploadSuccess'));
    } catch (error) {
      toast.error(getApiErrorMessage(error, t('fileUploadError')));
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async () => {
    if (!logoPath) return;
    if (confirmRemove && !window.confirm(t('confirmRemoveBuildingLogo'))) {
      return;
    }

    setRemoving(true);
    try {
      if (onRemove) {
        await onRemove();
      } else {
        onLogoPathChange('');
      }
      toast.success(t('buildingLogoRemoveSuccess'));
    } catch (error) {
      toast.error(getApiErrorMessage(error, t('fileUploadError')));
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div className="space-y-3">
      <ImageUploadField
        label={label}
        imageUrl={logoPath || undefined}
        uploading={uploading}
        removing={removing}
        disabled={uploadDisabled}
        onUpload={handleUpload}
        onRemove={handleRemove}
        hint={t('buildingLogoUploadHint')}
        fit="contain"
      />

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('buildingLogoUrlLabel')}
        </label>
        <p className="mb-2 text-xs text-gray-500">{pathHint}</p>
        <input
          type="text"
          value={logoPath || ''}
          onChange={(e) => onLogoPathChange(e.target.value)}
          placeholder={placeholder}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>
    </div>
  );
};
